/**
 * Parameter editing panel for system settings and selected canvas components.
 * @module HybridSystemIntegration/components/ParameterPanel
 */

import React from 'react';
import {
  Button,
  Card,
  Divider,
  InputNumber,
  Select,
  Slider,
  Space,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  COMPONENT_LIBRARY,
  COMPONENT_TYPES,
  CONTROL_STRATEGIES,
  INSULATION_PRESETS,
} from '../constants';
import styles from '../HybridSystemIntegration.module.css';

const { Text, Title } = Typography;

function SliderField({ label, value, min, max, step = 1, unit = '', onChange }) {
  return (
    <div className={styles.sliderField}>
      <div className={styles.sliderLabel}>
        <Text>{label}</Text>
        <Text strong>{value}{unit}</Text>
      </div>
      <Slider min={min} max={max} step={step} value={value} onChange={onChange} />
    </div>
  );
}

function ComponentSpecEditor({ selectedNode, onUpdateSpec, onRemove }) {
  const { t } = useTranslation();
  if (!selectedNode) {
    return null;
  }

  const component = COMPONENT_LIBRARY[selectedNode.type];
  const specs = selectedNode.specs;
  const canRemove = ![COMPONENT_TYPES.BUILDING_LOAD, COMPONENT_TYPES.SMART_CONTROLLER].includes(selectedNode.type);
  const update = (key) => (value) => onUpdateSpec(selectedNode.id, key, value);

  return (
    <div className={styles.specEditor}>
      <div className={styles.selectedComponentHeader}>
        <span className={styles.selectedIcon} style={{ backgroundColor: component.color }}>
          {component.icon}
        </span>
        <div>
          <Text strong>{t(component.translationKey)}</Text>
          <br />
          <Text type="secondary">{t(component.categoryTranslationKey)}</Text>
        </div>
      </div>

      {[COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP, COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP].includes(selectedNode.type) && (
        <>
          <SliderField
            label={t('hybridSystemTool.controls.ratedCapacity')}
            value={specs.ratedCapacityKw}
            min={3}
            max={24}
            step={0.5}
            unit=" kW"
            onChange={update('ratedCapacityKw')}
          />
          <SliderField
            label={t('hybridSystemTool.controls.supplyTemperature')}
            value={specs.supplyTemperature}
            min={30}
            max={65}
            unit=" C"
            onChange={update('supplyTemperature')}
          />
          <SliderField
            label={t('hybridSystemTool.controls.bivalencePoint')}
            value={specs.bivalenceTemperature}
            min={-15}
            max={8}
            unit=" C"
            onChange={update('bivalenceTemperature')}
          />
        </>
      )}

      {selectedNode.type === COMPONENT_TYPES.GAS_BOILER && (
        <>
          <SliderField
            label={t('hybridSystemTool.controls.ratedCapacity')}
            value={specs.ratedCapacityKw}
            min={5}
            max={35}
            step={0.5}
            unit=" kW"
            onChange={update('ratedCapacityKw')}
          />
          <SliderField
            label={t('hybridSystemTool.controls.boilerEfficiency')}
            value={Math.round(specs.efficiency * 100)}
            min={70}
            max={98}
            unit="%"
            onChange={(value) => onUpdateSpec(selectedNode.id, 'efficiency', value / 100)}
          />
        </>
      )}

      {selectedNode.type === COMPONENT_TYPES.SOLAR_PV && (
        <SliderField
          label={t('hybridSystemTool.controls.pvCapacity')}
          value={specs.capacityKw}
          min={0}
          max={20}
          step={0.5}
          unit=" kWp"
          onChange={update('capacityKw')}
        />
      )}

      {selectedNode.type === COMPONENT_TYPES.THERMAL_STORAGE && (
        <>
          <SliderField
            label={t('hybridSystemTool.controls.storageCapacity')}
            value={specs.capacityKwh}
            min={2}
            max={60}
            unit=" kWh"
            onChange={update('capacityKwh')}
          />
          <SliderField
            label={t('hybridSystemTool.controls.storageDischarge')}
            value={specs.maxDischargeKw}
            min={1}
            max={10}
            step={0.5}
            unit=" kW"
            onChange={update('maxDischargeKw')}
          />
        </>
      )}

      {selectedNode.type === COMPONENT_TYPES.SMART_CONTROLLER && (
        <Text type="secondary">{t('hybridSystemTool.controls.controllerHint')}</Text>
      )}

      {selectedNode.type === COMPONENT_TYPES.BUILDING_LOAD && (
        <Text type="secondary">{t('hybridSystemTool.controls.buildingHint')}</Text>
      )}

      {canRemove && (
        <Button danger icon={<DeleteOutlined />} onClick={() => onRemove(selectedNode.id)}>
          {t('hybridSystemTool.actions.removeComponent')}
        </Button>
      )}
    </div>
  );
}

/**
 * Parameter panel for system-wide and component-specific settings.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} Parameter card.
 */
export function ParameterPanel({
  selectedNode,
  settings,
  strategy,
  storageLevel,
  onOpenPalette,
  onUpdateSetting,
  onUpdateSpec,
  onStrategyChange,
  onStorageLevelChange,
  onRemoveComponent,
  onResetSystem,
}) {
  const { t } = useTranslation();
  const strategyOptions = Object.values(CONTROL_STRATEGIES).map((item) => ({
    label: t(item.translationKey),
    value: item.key,
  }));
  const insulationOptions = Object.values(INSULATION_PRESETS).map((item) => ({
    label: t(item.translationKey),
    value: item.key,
  }));

  return (
    <Card className={styles.sideCard}>
      <div className={styles.panelHeaderInline}>
        <Title level={4}>{t('hybridSystemTool.controls.title')}</Title>
        <Space size="small">
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenPalette}>
            {t('hybridSystemTool.actions.addComponent')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={onResetSystem}>
            {t('hybridSystemTool.actions.reset')}
          </Button>
        </Space>
      </div>

      <div className={styles.formField}>
        <Text strong>{t('hybridSystemTool.controls.strategy')}</Text>
        <Select
          value={strategy}
          options={strategyOptions}
          onChange={onStrategyChange}
        />
      </div>

      <Divider />
      <Title level={5}>{t('hybridSystemTool.controls.systemAssumptions')}</Title>

      <SliderField
        label={t('hybridSystemTool.controls.outdoorTemperature')}
        value={settings.outdoorTemperature}
        min={-18}
        max={18}
        unit=" C"
        onChange={(value) => onUpdateSetting('outdoorTemperature', value)}
      />
      <SliderField
        label={t('hybridSystemTool.controls.floorArea')}
        value={settings.floorArea}
        min={45}
        max={420}
        step={5}
        unit=" m2"
        onChange={(value) => onUpdateSetting('floorArea', value)}
      />
      <div className={styles.formField}>
        <Text>{t('hybridSystemTool.controls.insulationLevel')}</Text>
        <Select
          value={settings.insulationLevel}
          options={insulationOptions}
          onChange={(value) => onUpdateSetting('insulationLevel', value)}
        />
      </div>
      <SliderField
        label={t('hybridSystemTool.controls.storageLevel')}
        value={storageLevel}
        min={0}
        max={100}
        unit="%"
        onChange={onStorageLevelChange}
      />
      <SliderField
        label={t('hybridSystemTool.controls.solarIrradiance')}
        value={Math.round(settings.solarIrradiance * 100)}
        min={0}
        max={100}
        unit="%"
        onChange={(value) => onUpdateSetting('solarIrradiance', value / 100)}
      />

      <Divider />
      <Title level={5}>{t('hybridSystemTool.controls.prices')}</Title>
      <Space.Compact block className={styles.priceInputs}>
        <InputNumber
          addonBefore={t('hybridSystemTool.controls.electricity')}
          min={0.05}
          max={1.2}
          step={0.01}
          value={settings.electricityPrice}
          onChange={(value) => onUpdateSetting('electricityPrice', value ?? 0)}
        />
        <InputNumber
          addonBefore={t('hybridSystemTool.controls.gas')}
          min={0.02}
          max={0.4}
          step={0.01}
          value={settings.gasPrice}
          onChange={(value) => onUpdateSetting('gasPrice', value ?? 0)}
        />
      </Space.Compact>

      <Divider />
      <Title level={5}>{t('hybridSystemTool.controls.selectedComponent')}</Title>
      <ComponentSpecEditor
        selectedNode={selectedNode}
        onUpdateSpec={onUpdateSpec}
        onRemove={onRemoveComponent}
      />
    </Card>
  );
}

export default ParameterPanel;
